import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import NRReactGrid from '../../components/NRReactGrid/NRReactGrid';
import { NRSBOMNameProvider } from '../../components/NRReactGridCustomComponents/NRCustomProviders/NRSBOMNameProvider';
import { NRSBOMVersionProvider } from '../../components/NRReactGridCustomComponents/NRCustomProviders/NRSBOMVersionProvider';
import { NRSBOMTypeProvider } from '../../components/NRReactGridCustomComponents/NRCustomProviders/NRSBOMTypeProvider';
import { CONSTANTS } from '../../constants/constants';
import { NRSBOMLicenseProvider } from '../../components/NRReactGridCustomComponents/NRCustomProviders/NRSBOMLicenseProvider';

/**
 * Component to render SBOM components in a table format using NRReactGrid
 * @param {Array} components - Array of component objects with name, type, vendor, version
 */

const { ROWS } = CONSTANTS.TABLE.SBOM_TABLE;

export const sbom_columns = [
  {
    title: { value: ROWS.NAME },
    name: 'name',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.VERSION },
    name: 'version',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.VENDOR },
    name: 'vendor',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.TYPE },
    name: 'type',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.LICENSE },
    name: 'license',
    sortDisabled: true,
    getCellValue: row => (row.license ? row.license.join(', ') : undefined)
  }
];

export default function NRRaiseAISBOMTable({ components }) {
  const theme = useTheme();

  // Transform component data to match the expected format for NRReactGrid
  const transformedRows = useMemo(() => {
    if (!components || !Array.isArray(components) || components.length === 0) {
      return [];
    }

    return components.map((component, index) => {
      // Normalize type value - remove COMPONENT_TYPE_ prefix if present
      const typeValue = component.type || '';
      const normalizedType = typeValue.replace(/^COMPONENT_TYPE_/, '');

      // Get license array
      const licenseArray = component.licenses || component.license || [];
      const license = Array.isArray(licenseArray) ? licenseArray : [];

      return {
        id: `component-${index}`,
        name: component.name || '-',
        type: normalizedType,
        vendor: component.vendor || '-',
        version: component.version || '-',
        license: license,
        // Add dependency object structure for providers that might need it
        dependency: {
          name: component?.name || '-',
          type: normalizedType,
          package: {
            type: component?.packageType || component?.package?.type || null
          },
          vendor: component?.vendor || '-',
          version: {
            id: component?.version || '-'
          }
        }
      };
    });
  }, [components]);

  if (!components || !Array.isArray(components) || components.length === 0) {
    return null;
  }

  const columnExtensions = [
    { columnName: 'version', maxWidth: 150 },
    { columnName: 'type', width: 120 }
  ];

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.secondary }}>
        Components ({components.length})
      </Typography>
      <NRReactGrid
        rows={transformedRows}
        columns={sbom_columns}
        columnExtensions={columnExtensions}
        providersArray={[
          <NRSBOMNameProvider key="name" for={['name']} />,
          <NRSBOMVersionProvider key="version" for={['version']} />,
          <NRSBOMTypeProvider key="type" for={['type']} />,
          <NRSBOMLicenseProvider key="license" for={['license']} />
        ]}
        embedded
        showSelectAll={false}
        showIntegratedSelection={false}
        showIntegratedPagination={false}
        sx={{
          '& > div': {
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      />
    </Box>
  );
}
